import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getAuthToken, getAuthUser, getCurrentShopId } from "./auth";
import "./DashboardPage.css";

const API_URL = process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

const getTodayDate = () => {
  return new Date().toISOString().slice(0, 10);
};

const parsePaymentBreakdown = (sale) => {
  try {
    if (typeof sale.payment_breakdown === "string") {
      return JSON.parse(sale.payment_breakdown || "{}");
    }

    return sale.payment_breakdown || sale.paymentBreakdown || {};
  } catch {
    return {};
  }
};

const parseSaleItems = (sale) => {
  try {
    return typeof sale.items === "string"
      ? JSON.parse(sale.items || "[]")
      : sale.items || [];
  } catch {
    return [];
  }
};

const isRefundSale = (sale) => {
  if (!sale) return false;

  if (sale.is_refund === true) return true;
  if (sale.is_refund === "true") return true;

  const paymentMethod = String(
    sale.payment_method || sale.paymentMethod || ""
  ).toLowerCase();

  if (paymentMethod === "refund") return true;

  if (Number(sale.total || 0) < 0) return true;

  const items = parseSaleItems(sale);

  return Array.isArray(items) && items.some(
    (item) => item.is_refund || item.line_type === "refund"
  );
};

const getSalePaymentTotals = (sale) => {
  const breakdown = parsePaymentBreakdown(sale);

  const saleTotal = Number(sale.total || 0);

  const cashPaid = Number(
    breakdown.cash ||
      breakdown.cashPaid ||
      breakdown.cash_paid ||
      0
  );

  const cardPaid = Number(
    breakdown.card ||
      breakdown.cardPaid ||
      breakdown.card_paid ||
      0
  );

  const change = Number(
    breakdown.change ||
      breakdown.changeDue ||
      breakdown.change_due ||
      0
  );

  let cashTotal = Math.max(0, cashPaid - change);
  let cardTotal = cardPaid;

  // Fallback for older sales that may not have payment_breakdown
  if (cashPaid === 0 && cardPaid === 0) {
    const paymentMethod = String(
      sale.payment_method || sale.paymentMethod || ""
    ).toLowerCase();

    if (paymentMethod === "cash") {
      cashTotal = saleTotal;
      cardTotal = 0;
    }

    if (paymentMethod === "card") {
      cashTotal = 0;
      cardTotal = saleTotal;
    }
  }

  return {
    cash: cashTotal,
    card: cardTotal,
    change,
  };
};

const getSalesArray = (payload) => {
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload?.sales)) return payload.sales;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;

  return [];
};

const calculateSummaryFromSales = (sales) => {
  const initialSummary = {
    grossSales: 0,
    refundTotal: 0,
    netSales: 0,
    salesTransactions: 0,
    refundTransactions: 0,
    cashTotal: 0,
    cardTotal: 0,
  };

  if (!Array.isArray(sales) || sales.length === 0) {
    return initialSummary;
  }

  const calculatedSummary = sales.reduce((acc, sale) => {
    // Ignore voided transactions completely
    if (sale.is_void === true || sale.is_void === "true") {
      return acc;
    }

    const saleTotal = Number(sale.total || 0);
    const refund = isRefundSale(sale);

    if (refund) {
      acc.refundTransactions += 1;
      acc.refundTotal += Math.abs(saleTotal);

      return acc;
    }

    // Normal sale
    const payment = getSalePaymentTotals(sale);

    acc.salesTransactions += 1;
    acc.grossSales += Math.max(0, saleTotal);
    acc.cashTotal += Math.max(0, Number(payment.cash || 0));
    acc.cardTotal += Math.max(0, Number(payment.card || 0));

    return acc;
  }, initialSummary);

  calculatedSummary.netSales =
    calculatedSummary.grossSales - calculatedSummary.refundTotal;

  return calculatedSummary;
};

function DashboardPage() {
  const [user, setUser] = useState(null);
  const [shopId, setShopId] = useState(null);
const [summary, setSummary] = useState({
  grossSales: 0,
  refundTotal: 0,
  netSales: 0,
  salesTransactions: 0,
  refundTransactions: 0,
  cashTotal: 0,
  cardTotal: 0,
});
 const [licence, setLicence] = useState(null);

const [tills, setTills] = useState([]);
const [selectedTillId, setSelectedTillId] = useState("all");
const [tillsLoading, setTillsLoading] = useState(false);

const [loading, setLoading] = useState(true);
const [shopName, setShopName] = useState("");

const loadTills = useCallback(async () => {
  try {
    setTillsLoading(true);

    const token = getAuthToken();

    const response = await axios.get(`${API_URL}/tills`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const tillList = Array.isArray(response.data?.tills)
      ? response.data.tills
      : [];

    setTills(tillList);
  } catch (err) {
    console.error(
      "Dashboard till load error:",
      err.response?.data || err.message
    );

    setTills([]);
  } finally {
    setTillsLoading(false);
  }
}, []);

const loadShopDetails = useCallback(async () => {
  try {
    const token = getAuthToken();
    const activeShopId = getCurrentShopId();

    if (!activeShopId) {
      setShopName("");
      return;
    }

    const response = await axios.get(
      `${API_URL}/shop-settings`,
      {
        params: {
          shop_id: activeShopId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setShopName(
      response.data?.shop_table_name ||
        response.data?.shop_name ||
        response.data?.name ||
        ""
    );
  } catch (err) {
    console.error(
      "Dashboard shop load error:",
      err.response?.data || err.message
    );

    setShopName("");
  }
}, []);



  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);

      const today = getTodayDate();
      const authUser = getAuthUser();
      const activeShopId = getCurrentShopId();

      console.log("Dashboard auth user:", authUser);
      console.log("Dashboard current admin shop id:", activeShopId);

      setUser(authUser);
      setShopId(activeShopId);

    const token = getAuthToken();

if (!token) {
  throw new Error(
    "Your login session has expired. Please log in again."
  );
}

const salesRes = await axios.get(`${API_URL}/sales`, {
  params: {
    shop_id: activeShopId,
    from: today,
    to: today,
    till_id:
      selectedTillId === "all"
        ? "all"
        : Number(selectedTillId),
  },
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

      console.log("DASHBOARD SALES FOR SHOP:", activeShopId);
      console.log("DASHBOARD SALES DATA:", salesRes.data);

      const sales = getSalesArray(salesRes.data);
    const fixedSummary = calculateSummaryFromSales(sales);

      console.log("DASHBOARD FIXED SUMMARY:", fixedSummary);

      setSummary(fixedSummary);

      try {
        const licenceRes = await axios.get(`${API_URL}/license/check`, {
          params: {
            shop_id: activeShopId,
          },
        });

        console.log("DASHBOARD LICENCE FOR SHOP:", activeShopId);
        console.log("DASHBOARD LICENCE DATA:", licenceRes.data);

        setLicence(licenceRes.data);
      } catch (licenceErr) {
        console.error(
          "Dashboard licence check error:",
          licenceErr.response?.data || licenceErr.message
        );

        setLicence({
          valid: false,
          reason:
            licenceErr.response?.data?.reason ||
            licenceErr.response?.data?.error ||
            "Licence check failed",
        });
      }
    } catch (err) {
      console.error("Dashboard load error:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
 }, [selectedTillId]);

 useEffect(() => {
  loadTills();
}, [loadTills]);

useEffect(() => {
  loadDashboard();
}, [loadDashboard]);

useEffect(() => {
  loadShopDetails();
}, [loadShopDetails]);

const getSelectedTillLabel = () => {
  if (selectedTillId === "all") {
    return "All Tills";
  }

  const selectedTill = tills.find(
    (till) =>
      String(till.id) === String(selectedTillId)
  );

  if (!selectedTill) {
    return "Selected Till";
  }

  return `${selectedTill.name} (${selectedTill.till_code})`;
};

  return (
    <div className="dashboard-page">
    <div className="dashboard-header">
  <div className="dashboard-header-copy">
    <span className="dashboard-eyebrow">Shop Dashboard</span>

    <h1>Welcome back{user?.name ? `, ${user.name}` : ""}</h1>

    <p>
      Here is today&apos;s performance for{" "}
      <strong>
       {shopName || user?.shop_name || "Shop"}
      </strong>
      .
    </p>
  </div>

  <div className="dashboard-header-actions">
    <label className="dashboard-till-filter">
      <span>Viewing</span>

      <select
        value={selectedTillId}
        onChange={(e) => setSelectedTillId(e.target.value)}
        disabled={tillsLoading}
      >
        <option value="all">All Tills</option>

        {tills.map((till) => (
          <option key={till.id} value={String(till.id)}>
            {till.name} ({till.till_code})
          </option>
        ))}
      </select>
    </label>

    <button
      type="button"
      onClick={loadDashboard}
      disabled={loading}
    >
      {loading ? "Loading..." : "Refresh"}
    </button>
  </div>
</div>

<div className="dashboard-status-strip">
  <div>
    <span>Current shop</span>
    <strong>
     {shopName || user?.shop_name || "Shop"}
    </strong>
  </div>

  <div>
    <span>Selected till</span>
    <strong>{getSelectedTillLabel()}</strong>
  </div>

  <div>
    <span>Licence</span>
    <strong
      className={licence?.valid ? "good-text" : "bad-text"}
    >
      {licence?.valid ? "Active" : "Inactive"}
    </strong>
  </div>
</div>

<div className="dashboard-primary-cards">
  <div className="dashboard-stat-card dashboard-stat-primary">
    <span>Net Sales</span>

    <strong>
      €{Number(summary.netSales || 0).toFixed(2)}
    </strong>

    <small>Gross sales minus refunds</small>
  </div>

  <div className="dashboard-stat-card">
    <span>Transactions</span>

    <strong>{summary.salesTransactions || 0}</strong>

    <small>Completed sales today</small>
  </div>

  <div className="dashboard-stat-card">
    <span>Average Sale</span>

    <strong>
      €
      {summary.salesTransactions
        ? (
            Number(summary.grossSales || 0) /
            Number(summary.salesTransactions)
          ).toFixed(2)
        : "0.00"}
    </strong>

    <small>Average value per transaction</small>
  </div>

  <div className="dashboard-stat-card dashboard-stat-refund">
    <span>Refunds</span>

    <strong>
      €{Number(summary.refundTotal || 0).toFixed(2)}
    </strong>

    <small>
      {summary.refundTransactions || 0} refund transactions
    </small>
  </div>
</div>

<div className="dashboard-main-grid">
  <section className="dashboard-panel dashboard-sales-panel">
    <div className="dashboard-panel-heading">
      <div>
        <span className="dashboard-panel-label">
          Today&apos;s Payments
        </span>

        <h2>Sales Breakdown</h2>
      </div>

      <span className="dashboard-panel-till">
        {getSelectedTillLabel()}
      </span>
    </div>

    <div className="dashboard-payment-grid">
      <div className="dashboard-payment-card">
        <span>Gross Sales</span>

        <strong>
          €{Number(summary.grossSales || 0).toFixed(2)}
        </strong>
      </div>

      <div className="dashboard-payment-card">
        <span>Cash Sales</span>

        <strong>
          €{Number(summary.cashTotal || 0).toFixed(2)}
        </strong>
      </div>

      <div className="dashboard-payment-card">
        <span>Card Sales</span>

        <strong>
          €{Number(summary.cardTotal || 0).toFixed(2)}
        </strong>
      </div>
    </div>

    <div className="dashboard-payment-summary">
      <div>
        <span>Cash share</span>

        <strong>
          {summary.grossSales
            ? `${(
                (Number(summary.cashTotal || 0) /
                  Number(summary.grossSales)) *
                100
              ).toFixed(0)}%`
            : "0%"}
        </strong>
      </div>

      <div>
        <span>Card share</span>

        <strong>
          {summary.grossSales
            ? `${(
                (Number(summary.cardTotal || 0) /
                  Number(summary.grossSales)) *
                100
              ).toFixed(0)}%`
            : "0%"}
        </strong>
      </div>
    </div>
  </section>

  <section className="dashboard-panel">
    <div className="dashboard-panel-heading">
      <div>
        <span className="dashboard-panel-label">
          Shortcuts
        </span>

        <h2>Quick Actions</h2>
      </div>
    </div>

    <div className="quick-actions">
      <button
        type="button"
        onClick={() => (window.location.hash = "/inventory")}
      >
        <span>Products</span>
        <small>Manage inventory</small>
      </button>

      <button
        type="button"
        onClick={() => (window.location.hash = "/sales")}
      >
        <span>Sales</span>
        <small>View transactions</small>
      </button>

      <button
        type="button"
        onClick={() => (window.location.hash = "/reports")}
      >
        <span>Reports</span>
        <small>Business insights</small>
      </button>

      <button
        type="button"
        onClick={() =>
          (window.location.hash = "/shop-settings")
        }
      >
        <span>Settings</span>
        <small>Configure shop</small>
      </button>
    </div>
  </section>
</div>

<div className="dashboard-bottom-grid">
  <section className="dashboard-panel">
    <div className="dashboard-panel-heading">
      <div>
        <span className="dashboard-panel-label">
          Account
        </span>

        <h2>Licence Details</h2>
      </div>
    </div>

    <div className="dashboard-row">
      <span>Status</span>

      <strong
        className={licence?.valid ? "good-text" : "bad-text"}
      >
        {licence?.valid ? "Valid" : "Invalid"}
      </strong>
    </div>

    <div className="dashboard-row">
      <span>Plan</span>

      <strong>
        {licence?.plan_name || user?.plan_name || "-"}
      </strong>
    </div>

    <div className="dashboard-row">
      <span>Expiry</span>

      <strong>
        {licence?.expires_at
          ? new Date(licence.expires_at).toLocaleDateString()
          : "-"}
      </strong>
    </div>

    <div className="dashboard-row">
      <span>Mode</span>
      <strong>Cloud Admin</strong>
    </div>
  </section>

  <section className="dashboard-panel">
    <div className="dashboard-panel-heading">
      <div>
        <span className="dashboard-panel-label">
          Session
        </span>

        <h2>Account Details</h2>
      </div>
    </div>

    <div className="dashboard-row">
      <span>User</span>
      <strong>{user?.name || "-"}</strong>
    </div>

    <div className="dashboard-row">
      <span>Role</span>
      <strong>{user?.role || "-"}</strong>
    </div>

    <div className="dashboard-row">
      <span>Shop ID</span>
      <strong>{shopId || "-"}</strong>
    </div>

    <div className="dashboard-row">
      <span>Tills available</span>
      <strong>{tills.length}</strong>
    </div>
  </section>
</div>
    </div>
  );
}

export default DashboardPage;